import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, ValidationErrors } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import moment, { HTML5_FMT } from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ODataModelChangeEvent } from '../../interfaces/odata-model.interface';
import { DateModel } from '../../models/primitive/date.model';
import { DateUtilities } from '../../utilities/date-utilities';

@Component({
    selector: 'pace-core-date-input',
    templateUrl: './date-input.component.html',
    styleUrls: ['./date-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
})
export class DateInputComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public model: DateModel;

    @Output()
    public modelChange = new EventEmitter<DateModel>();

    @Input()
    public parentForm: FormArray | undefined;

    @Input()
    public ariaLabel = 'Date Input';

    @Output()
    public valueChange = new EventEmitter<ODataModelChangeEvent<DateModel>>();

    @ViewChild('dateInput')
    public dateInput: ElementRef<HTMLInputElement>;

    public dateInputControl: FormControl;

    public faCalendar = faCalendar;

    private originalDate: moment.Moment;

    private destroySubject = new Subject<void>();

    private dateFormat: string;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {}

    public ngOnInit(): void {
        this.dateFormat = this.model.options.displayOnlyMonthYear ? HTML5_FMT.MONTH : HTML5_FMT.DATE;
        this.originalDate = this.model.value.clone();

        const dateValidator = (control: AbstractControl): ValidationErrors | null => this.checkValidity(control);
        this.dateInputControl = this.formBuilder.control(
            {
                value: this.model.value.isValid() ? this.model.value.format(this.dateFormat) : undefined,
                disabled: this.model.options.readonly,
            },
            dateValidator,
        );

        this.dateInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    if (this.dateInputControl.dirty) {
                        const oldValue = this.model.value.clone();
                        if (this.dateInputControl.errors) {
                            const resetValue = this.originalDate.isValid() ? this.originalDate : oldValue;
                            if (newVal === '' && resetValue.isValid()) {
                                this.dateInputControl.setValue(resetValue.format(this.dateFormat));
                            }
                        }

                        this.model.value = DateUtilities.setDateHtml5(
                            this.model.value,
                            <string>this.dateInputControl.value,
                            this.model.options.displayOnlyMonthYear,
                        );
                        this.modelChange.emit(this.model);
                        this.valueChange.emit({ oldValue: oldValue, newValue: this.model.value.clone() });
                    }
                }),
                takeUntil(this.destroySubject),
            )
            .subscribe();

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.dateInputControl);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'Error in DateInputComponent -> ngOnInit -> parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj && changesObj.model) {
            this.model = <DateModel>changesObj.model.currentValue;

            if (this.dateInputControl) {
                const modelValue = this.model.value.format(this.dateFormat);

                // only update the input field value if it actually changed to avoid redundant change detection
                if (
                    !(<DateModel>changesObj.model.previousValue).value.isSame(this.model.value) ||
                    this.dateInputControl.value !== modelValue
                ) {
                    this.dateInputControl.setValue(modelValue);
                }

                if (this.model.options.readonly && this.dateInputControl.enabled) {
                    this.dateInputControl.disable({ onlySelf: true, emitEvent: false });
                }
                if (!this.model.options.readonly && this.dateInputControl.disabled) {
                    this.dateInputControl.enable({ onlySelf: true, emitEvent: false });
                }
            }
        }
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.dateInputControl);
            this.parentForm.removeAt(index);
        }
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    public checkValidity(control: AbstractControl): ValidationErrors | null {
        const valueClone = this.model.value.isValid() ? this.model.value.clone() : moment();

        const controlValue = <string>control.value;
        const potentialDate = DateUtilities.setDateHtml5(valueClone, controlValue);

        const errors: ValidationErrors = {};

        if (!controlValue && this.model.options.required) {
            errors.requiredDate = { text: 'Date is not selected' };
        }

        if (controlValue === 'Invalid date' && this.model.options.required) {
            errors.invalidDate = { text: 'Please select date' };
        }

        if (potentialDate.isBefore(this.model.options.minValue, 'day')) {
            errors.minValue = {
                text: `${this.translate.instant('DATE_INPUT_SELECT_DATE_RANGE_TEXT')} ${this.translate.instant(
                    'DATE_INPUT_VALID_DATE_RANGE_FROM_TEXT',
                    {
                        minDate: this.model.options.minValue.format(this.clientManager.getDateFormat()),
                    },
                )}`,
            };
        }
        if (potentialDate.isAfter(this.model.options.maxValue, 'day')) {
            errors.maxValue = {
                text: `${this.translate.instant('DATE_INPUT_SELECT_DATE_RANGE_TEXT')} ${this.translate.instant(
                    'DATE_INPUT_VALID_DATE_RANGE_UPTO_TEXT',
                    {
                        maxDate: this.model.options.maxValue.format(this.clientManager.getDateFormat()),
                    },
                )}`,
            };
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public openCalendar(): void {
        this.dateInput.nativeElement.showPicker();
    }
}
